import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router';
import { useEffect, useState, ReactNode } from 'react';
import { onAccountStatusChanged } from '@/lib/accountEvents';
import { supabase } from '@/integrations/supabase/client';
import { DEMO_MODE } from '@/lib/demoMode';
import { logger } from '@/lib/logger';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { userTypeMatches } from '@/lib/routeAccess';

interface ProtectedRouteProps {
  children: ReactNode;
  requireCompleteProfile?: boolean;
  requiredUserType?: 'client' | 'helper' | 'admin';
  fallback?: ReactNode;
  redirectTo?: string;
}

interface ProfileCheck {
  loading: boolean;
  checkedUserId: string | null;
  profile: {
    profile_completed: boolean;
    is_helper: boolean;
    role?: string;
    profile_status?: string | null;
    account_status?: string | null;
  } | null;
  error: string | null;
}


const AccessDenied = ({
  message,
  onRetry,
  onGoBack
}: {
  message: string;
  onRetry?: () => void;
  onGoBack?: () => void;
}) => (
      <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-background to-muted/50 px-4">
    <div className="text-center space-y-6 max-w-md mx-auto">
      <div className="w-20 h-20 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
        <svg
          className="w-10 h-10 text-warning"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-6V9a2 2 0 00-2-2H8a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2z" />
        </svg>
      </div>

      <div className="space-y-2">
        <h2 className="h-page text-foreground">Access Restricted</h2>
        <p className="text-muted-foreground">{message}</p>
      </div>

      <div className="flex justify-center space-x-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center px-4 py-2 border border-primary text-sm font-medium rounded-md text-primary bg-transparent hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Try Again
          </button>
        )}
        {onGoBack && (
          <button
            onClick={onGoBack}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
          >
            Go Back
          </button>
        )}
      </div>
    </div>
  </div>
);

export function ProtectedRoute({
  children,
  requireCompleteProfile = true,
  requiredUserType,
  fallback,
  redirectTo
}: ProtectedRouteProps) {
  const { user, isLoading, initialized, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileCheck, setProfileCheck] = useState<ProfileCheck>({
    loading: true,
    checkedUserId: null,
    profile: null,
    error: null
  });

  // The account's own row can change under this guard: a self-service
  // reactivation on login (useAccountReactivation) lands after the read
  // below, and the cached 'deactivated' would refuse a now-active helper
  // until a reload (Codex on #1300). The hook announces it; re-read.
  const [profileVersion, setProfileVersion] = useState(0);
  useEffect(() => {
    // Also hears other tabs: a tab that lost the reactivation race reads
    // 'active' and announces nothing, so the winner's write must reach it.
    return onAccountStatusChanged(() => setProfileVersion((v) => v + 1));
  }, []);

  // Check profile when user is available
  useEffect(() => {
    if (DEMO_MODE) { setProfileCheck({ loading: false, checkedUserId: null, profile: null, error: null }); return; }
    if (!initialized || isLoading || !user) {
      setProfileCheck({ loading: false, checkedUserId: null, profile: null, error: null });
      return;
    }

    let cancelled = false;
    const checkProfile = async () => {
      try {
        setProfileCheck(prev => ({ ...prev, loading: true, checkedUserId: null, error: null }));

        const { data: profile, error } = await supabase
          .from('profiles')
          .select('profile_completed, is_helper, role, profile_status, account_status')
          .eq('id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        if (cancelled) return;
        setProfileCheck({
          loading: false,
          checkedUserId: user.id,
          profile: profile
            ? {
                profile_completed: profile.profile_completed ?? false,
                is_helper: profile.is_helper ?? false,
                role: profile.role ?? undefined,
                profile_status: profile.profile_status,
                account_status: profile.account_status,
              }
            : { profile_completed: false, is_helper: false },
          error: null
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Profile check error:', err);
        setProfileCheck({
          loading: false,
          checkedUserId: user.id,
          profile: null,
          error: err instanceof Error ? err.message : 'Failed to check profile'
        });
      }
    };

    void checkProfile();
    return () => { cancelled = true; };
  }, [user, initialized, isLoading, profileVersion]);

  // Handle authentication redirects
  useEffect(() => {
    if (DEMO_MODE) return;
    if (!initialized || isLoading) return;

    if (!user) {
      const currentPath = location.pathname + location.search;
      const redirectUrl = redirectTo || `/auth?redirect=${encodeURIComponent(currentPath)}`;
      navigate(redirectUrl, { replace: true });
      return;
    }

    // Handle auth errors
    if (authError) {
      console.error('Auth error in protected route:', authError);
      navigate('/auth', { replace: true });
    }
  }, [user, initialized, isLoading, authError, navigate, location, redirectTo]);

  // Handle profile-based redirects
  useEffect(() => {
    if (DEMO_MODE) return;
    if (!user || profileCheck.loading || !profileCheck.profile) return;

    const { profile_completed, is_helper, role } = profileCheck.profile;

    // Diagnostic — remove once routing is verified green for all three roles
    logger.debug('[protected-route] profile check', {
      pathname: location.pathname,
      profile_completed,
      is_helper,
      role,
      requireCompleteProfile,
    });

    // If profile is required but not complete, redirect to onboarding
    if (requireCompleteProfile && !profile_completed) {
      // Only redirect to onboarding if user is trying to be a helper
      // Clients don't need to complete onboarding
      if (is_helper && requiredUserType === 'helper') {
        logger.debug('[protected-route] bouncing to /onboarding from', location.pathname);
        navigate('/onboarding', { replace: true });
        return;
      }
    }

    // Role mismatch is handled in the render path (a clean AccessDenied screen
    // with a "Go Back" to the user's own dashboard) — not by auto-navigating
    // here, which would flash the denial then bounce the user to home.
  }, [
    user,
    profileCheck,
    requireCompleteProfile,
    requiredUserType,
    navigate,
    location.pathname
  ]);

  // DEMO MODE: Skip all auth/profile checks
  if (DEMO_MODE) {
    return <>{children}</>;
  }

  // Show loading states - only block if no user at all
  if (!initialized || isLoading) {
    return fallback || <LoadingSpinner message="Authenticating..." />;
  }

  // Show error states
  if (!user) {
    return fallback || <LoadingSpinner message="Redirecting to sign in..." />;
  }

  // Profile check runs in background - don't block rendering
  // Redirects happen via useEffect if needed

  if (authError) {
    return (
      <AccessDenied
        message="Authentication error. Please sign in again."
        onRetry={() => window.location.reload()}
        onGoBack={() => navigate('/auth')}
      />
    );
  }

  if (profileCheck.error) {
    return (
      <AccessDenied
        message="Unable to verify your permissions. Please try again."
        onRetry={() => window.location.reload()}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  // A changed auth session must not render the previous account's page while
  // its new profile check is pending, even before the effect above has run.
  if (requiredUserType && (profileCheck.loading || profileCheck.checkedUserId !== user.id)) {
    return fallback || <LoadingSpinner message="Verifying access..." />;
  }

  // Check profile requirements
  if (requireCompleteProfile && profileCheck.profile && !profileCheck.profile.profile_completed && profileCheck.profile.is_helper && requiredUserType === 'helper') {
    return fallback || <LoadingSpinner message="Redirecting to profile setup..." />;
  }

  // Check user type requirements
  if (requiredUserType && profileCheck.profile) {
    const { is_helper, profile_status } = profileCheck.profile;

    if (!userTypeMatches(requiredUserType, profileCheck.profile)) {
      let message = `This page is only available for ${requiredUserType}s.`;
      if (requiredUserType === 'helper' && !is_helper) {
        message = "This page is only available for service providers.";
      } else if (requiredUserType === 'client' && is_helper) {
        message = "This page is only available for clients.";
      }

      return (
        <AccessDenied
          message={message}
          onGoBack={() => {
            // Navigate to appropriate dashboard based on user type. A helper
            // whose profile_status isn't 'approved' yet would just bounce
            // right back into this same screen at '/helper-dashboard' —
            // send them to the application status page instead.
            const dashboardRoute = is_helper
              ? (profile_status === 'approved' ? '/helper-dashboard' : '/helper-profile')
              : '/client-dashboard';
            navigate(dashboardRoute, { replace: true });
          }}
        />
      );
    }
  }

  // All checks passed, render the protected content
  return <>{children}</>;
}

// Convenience wrapper for helper-only routes
export function HelperRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requiredUserType'>) {
  return (
    <ProtectedRoute {...props} requiredUserType="helper">
      {children}
    </ProtectedRoute>
  );
}

// Convenience wrapper for client-only routes
export function ClientRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requiredUserType'>) {
  return (
    <ProtectedRoute {...props} requiredUserType="client">
      {children}
    </ProtectedRoute>
  );
}

// Convenience wrapper for admin-only routes
export function AdminRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requiredUserType'>) {
  return (
    <ProtectedRoute {...props} requiredUserType="admin">
      {children}
    </ProtectedRoute>
  );
}
